import { useEffect, useRef, useState } from 'react';

/**
 * Estado que sobrevive à navegação entre telas, mas NÃO ao recarregar a página.
 *
 * O router desmonta o componente ao trocar de rota, e com ele morre todo
 * `useState`. Voltar para o inbox recolhia o acordeão; voltar para a agenda
 * jogava fora o PDF já lido. Isto guarda o valor num Map de módulo, vivo
 * enquanto a aba estiver aberta.
 *
 * **EM MEMÓRIA, NUNCA EM `localStorage`/`sessionStorage`** (PLANO seção 9 —
 * LGPD). A agenda carregada tem nome e telefone de paciente: no
 * `sessionStorage` isso ficaria escrito no disco do computador da recepção,
 * legível por qualquer um que abra o DevTools depois. Sumir no reload é o
 * comportamento desejado, não uma limitação.
 *
 * Sem mecanismo de assinatura de propósito: cada chave tem um leitor só, que
 * semeia o próprio `useState` na montagem. Um store observável aqui só
 * acrescentaria re-renderizações que ninguém precisa.
 */
const memoria = new Map();

/**
 * ★★★ WU/U2 — A CHAVE DO DISPARO DA AGENDA, com UMA grafia só.
 *
 * ⚠⚠ Ela estava escrita à mão dentro de `ConfirmarAgendaPage` (`CHAVES.envio`), e agora **o
 * AppShell precisa lê-la** para decidir se o botão "Atualizar" pode ser clicado: ★★★ recarregar
 * no meio de um disparo de 20 confirmações é o pior caso do sistema — **mensagem real já saiu
 * para parte das pessoas**, e o estado do lote vive em memória.
 *
 * ★ Duas grafias da mesma chave seriam duas chaves: a página gravaria numa e o shell leria
 * outra, e o botão ficaria habilitado no meio do disparo **sem ninguém perceber**.
 */
export const CHAVE_ENVIO_AGENDA = 'agenda:envio';

/** ★ O disparo da agenda está rodando AGORA? É o que trava o botão "Atualizar" (U2). */
export const disparoEmAndamento = () => Boolean(lerEstado(CHAVE_ENVIO_AGENDA, null)?.rodando);

export function lerEstado(chave, padrao) {
  return memoria.has(chave) ? memoria.get(chave) : padrao;
}

export function guardarEstado(chave, valor) {
  memoria.set(chave, valor);
}

/**
 * Esvazia tudo. **Obrigatório no logout.**
 *
 * O computador da recepção é compartilhado: sem isto, quem entrasse depois
 * navegaria até "Confirmar agenda" e encontraria a lista de pacientes de quem
 * usou antes — nome e telefone de todo mundo, sem ter carregado PDF nenhum.
 */
export function limparEstadoDeSessao() {
  memoria.clear();
}

/**
 * `useState` que se lembra do último valor entre montagens.
 *
 * @param chave    identifica o assunto. Trocar a chave recarrega do store, em
 *                 vez de arrastar o valor do assunto anterior — é o que faz a
 *                 conversa selecionada ser por canal, e não uma só global.
 * @param inicial  valor (ou função que o produz) para a primeira vez.
 */
export function useEstadoPreservado(chave, inicial) {
  const semear = () => (typeof inicial === 'function' ? inicial() : inicial);
  const [valor, setValor] = useState(() => lerEstado(chave, semear()));

  // Ajuste durante o render, e não num efeito: com efeito, o primeiro quadro
  // depois da troca de chave mostraria o valor da chave antiga.
  const chaveAnterior = useRef(chave);
  if (chaveAnterior.current !== chave) {
    chaveAnterior.current = chave;
    setValor(lerEstado(chave, semear()));
  }

  useEffect(() => {
    guardarEstado(chave, valor);
  }, [chave, valor]);

  return [valor, setValor];
}
